import { API } from "@swivl-mono/api-helper";
import { MemberInterface } from "@swivl-mono/types";
import { Button, ButtonVariant, SearchInput, Spinner } from "@swivl-mono/ui";
import { m } from "motion/react";
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";

import AccountModel from "../../../Models/Account/AccountModel";
import Logo from "../Assets/SwivlLogo.png";

interface AvatarsState {
  loaded: boolean;
  avatars: { [orgId: number]: string };
}

export function SelectOrg() {
  const [searchParams] = useSearchParams();
  const redirect = searchParams.get("redirect");
  const { memberships: allMemberships } = AccountModel.useState((s) => s);
  const memberships = useMemo(() => (allMemberships ?? []).filter((m) => m.org), [allMemberships]);
  const [avatarsState, setAvatarsState] = useState<AvatarsState>({ loaded: false, avatars: {} });
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    if (avatarsState.loaded || memberships.length === 0) return;

    API.Org.avatarsForOrgs(memberships.map((m) => m.org.id).join(","))
      .then((avatars) => {
        setAvatarsState({ loaded: true, avatars: avatars });
      })
      .catch(() => {
        setAvatarsState({ loaded: true, avatars: {} });
      });
  }, [avatarsState.loaded, memberships]);

  const sortedMemberships = useMemo(() => [...memberships].sort((a, b) => a.org.name.localeCompare(b.org.name)), [memberships]);

  const filteredMemberships = useMemo(() => {
    if (!searchQuery) return sortedMemberships;
    const query = searchQuery.toLowerCase();
    return sortedMemberships.filter((membership) => membership.org.name?.toLowerCase().includes(query));
  }, [sortedMemberships, searchQuery]);

  const handleSearchOrganization = (searchValue: string) => {
    setSearchQuery(searchValue);
  };

  const handleSelectMembership = (membership: MemberInterface): void => {
    AccountModel.selectMember(membership);
    // Full page load so the init sequence runs behind LoadingView before the
    // authenticated app renders (ENG-2836).
    window.location.href = redirect || "/dashboard";
  };

  return (
    <div className="flex w-full flex-1 flex-col items-center overflow-y-auto pt-4 pb-8">
      <img src={Logo} alt="Swivl Logo" className="mx-auto mb-8 w-52" />

      <h2 className="mb-4 text-center">Select Org</h2>

      <div className="my-4 w-1/2">
        <SearchInput
          autoFocus
          value={searchQuery}
          onChange={(e) => handleSearchOrganization(e.target.value)}
          name="search"
          placeholder="Search for an org..."
        />
      </div>

      <div className="mb-4 flex w-1/2 flex-col items-center justify-center space-y-3">
        {!filteredMemberships?.length && <>You have not joined any organizations. Check your email or contact your organization's administrators.</>}

        {filteredMemberships?.map((membership) => {
          let avatarComponent;
          if (avatarsState.loaded) {
            const url = avatarsState.avatars[membership.org.id]
              ? avatarsState.avatars[membership.org.id]
              : "https://swivl-cdn.s3-us-west-1.amazonaws.com/Hoover-teams-192.png";
            avatarComponent = <img key={membership.org.id} src={url} alt={membership.org.name} className="h-12 w-12 object-cover" />;
          } else {
            avatarComponent = (
              <div className="w-1/4">
                {" "}
                <Spinner />
              </div>
            );
          }

          return (
            <button
              key={membership.id}
              className="flex w-full cursor-pointer flex-row items-center justify-start bg-white shadow-sm"
              onClick={() => handleSelectMembership(membership)}
            >
              {avatarComponent}
              <div className="p-2">{membership.org.name}</div>
            </button>
          );
        })}
      </div>
      <div className="flex items-center justify-center">
        <Button variant={ButtonVariant.GHOST} onClick={() => AccountModel.logOut()}>
          Logout
        </Button>
      </div>
    </div>
  );
}
